import { computed, ref } from 'vue';

/**
 * Staleness thresholds for push-only progress entries (see the
 * staleness ticker inside agentChat): a non-terminal chain/creation
 * entry with no socket event for STALE_EVENT_MS is timed out locally
 * (its process is gone — a healthy step never goes this quiet), and a
 * successful handoff older than HANDOFF_MAX_MS stops claiming
 * "Finalising document…" (its follow-on evidently never landed).
 */
const STALE_EVENT_MS = 5 * 60 * 1000;
const HANDOFF_MAX_MS = 3 * 60 * 1000;

/**
 * Chat composable for the agent view — shared between rm-portal (Vue 2.7)
 * and rm-portal-beta (Vue 3). Pure logic; UI components are per-portal
 * because the frameworks + component libraries differ.
 *
 * On creation, fires an init() call to pre-load user context (hierarchy
 * overview, available tools, personalised greeting). The greeting becomes
 * the first visible assistant message. The context rides along in `history`
 * so Claude already knows the client's structure before the first real
 * question.
 *
 * Maintains two parallel views of the conversation:
 *   - `history`  — Anthropic-shaped message list round-tripped to the backend
 *   - `messages` — display-friendly list the UI renders
 *
 * While a turn is in progress, `composing` holds the assistant's in-flight
 * state. Today the REST transport populates `composing` in one shot when the
 * response arrives, then finalises it into `messages`. When socket streaming
 * arrives, the same applyToolEnd / applyTextDelta mutators will be invoked
 * incrementally by socket handlers — UI components don't change.
 *
 * @param {Object} config
 * @param {Object} config.http — axios-compatible http client
 *   (rm-portal passes raw axios, rm-portal-beta passes its configured `api`
 *   instance with auth interceptors). Must support get/post/patch/delete.
 * @param {Object} [config.events] — optional event-gateway composable
 *   ({ on, off, connected }). When provided, the composable subscribes to
 *   `agent:*` socket events and streams tokens into `composing.text` as
 *   Claude emits them. Without it, the UI still works via the REST response.
 */
export function agentChat({ http, events }) {
    if (!http) {
        throw new Error('agentChat: http client is required');
    }

    const self = {
        messages: ref([]),
        composing: ref(null),
        history: ref([]),
        status: ref('initialising'), // 'initialising' | 'idle' | 'sending' | 'error'
        error: ref(null),
        availableTools: ref([]),
        session: ref(null),
        conversationId: ref(_cryptoRandomId()),
        conversations: ref([]),
        /**
         * Conversations the current user has previously had against a
         * specific page surface (e.g. a given document). Populated by
         * `loadConversationsForContext` when a host page tells us which
         * surface to filter for. Kept separate from `conversations` so
         * the main sidebar list and the per-page panel don't fight
         * each other.
         */
        contextConversations: ref([]),
        /** Attachments that finished uploading, to be sent with the NEXT turn. */
        pendingAttachments: ref([]),
        /** Per-file upload state: { id, filename, size_bytes, status, error?, attachment }. */
        uploads: ref([]),
        /** Models the backend knows about — populated by loadModels() once per session. */
        availableModels: ref([]),
        /**
         * The model id this client is configured to use — display only.
         * Model choice is client policy (the `atlas_model` client setting,
         * resolved server-side); there is no user-level picker and nothing
         * is sent with chat requests.
         */
        currentModel: ref(null),
        /**
         * Live progress for in-flight prompt-chain executions during the
         * current turn, keyed by execution_id. Populated by
         * `agent:chain_progress` socket events; cleared when the turn
         * finalises. Consumers render one strip per entry in the composing
         * bubble.
         */
        chainProgress: ref({}),
        /**
         * Live progress for in-flight document-creation requests, keyed
         * by doc_creation_request_id. Populated by
         * `agent:doc_creation_progress` socket events. Rendered as a
         * second "Document Creation Agent" card in the right-hand panel,
         * visually distinguished from the Prompt Chain Agent by its
         * emerald palette.
         */
        docCreationProgress: ref({}),
        /**
         * Chain outputs that are "ready to create" — the narration has
         * landed with `has_document_output: true` but the user hasn't
         * yet clicked Run (neither the chat-bubble CTA nor the panel
         * strip). Keyed by the chain's execution_id. Populated when an
         * `agent:background_message` arrives with the flag; cleared when
         * the user kicks off a creation (via REST) or dismisses it
         * manually from the panel. Lets the Document Creation Agent
         * card surface a "Ready — [Run]" row for each pending creation
         * so users can fire them from the panel without scrolling the
         * chat to find the bubble CTA.
         */
        readyToCreate: ref({}),
        /**
         * Optional "what the user is currently looking at" hint, sent on
         * every `sendMessage` call. Page hosts mutate this ref as the
         * user navigates — e.g. the document viewer sets
         * `{ kind: 'document', summary: 'Viewing RM-12345', data: {…} }`
         * when the doc loads and clears it on unmount. Ephemeral on the
         * wire (not persisted into history), so setting it doesn't
         * contaminate future turns' context.
         */
        pageContext: ref(null),
        /**
         * The host SPA's current route/page name (e.g. "alljobs",
         * "viewdoc"). Hosts keep it in step with their router; sent on
         * every turn for provenance (guided-workflow instances record
         * where they were actioned) — unlike pageContext it carries no
         * model-facing content.
         */
        routeName: ref(null),
        /**
         * Most recent `page_action` envelope from the backend. The agent
         * can return a `{ page_action: { kind, payload } }` field on a
         * /agent/chat response to ask a host page to apply something
         * to its current view (e.g. filter alljobs by a list of doc_ids
         * the agent just found). Surfaced as a ref so portal-side wiring
         * (rm-portal's atlasFlyout singleton) can watch it and route the
         * action through a per-page handler registry — without this
         * composable needing to know which pages exist or what they do.
         *
         * Set to `null` on every response that doesn't carry an action,
         * AND on every new response that does — so consumers wrapping
         * a `watch()` see a transition even when two identical actions
         * arrive back-to-back. (Server always sends a fresh object
         * reference, so referential equality isn't a concern in practice
         * — the null-cycle is belt-and-braces.)
         */
        lastPageAction: ref(null),
        /**
         * Context-window telemetry from the most recent turn, used to render
         * a "context used" meter so we can confirm server-side context editing
         * (A) is holding the prompt below the model's window. Shape mirrors the
         * backend `ChatResponse.context_usage`:
         *   { input_tokens, cache_read_input_tokens, cache_creation_input_tokens,
         *     total_tokens, context_window, cleared_input_tokens }
         * Null until the first turn completes.
         */
        contextUsage: ref(null),
        /**
         * Monthly usage-allowance state from the server, present only when
         * the client has an Atlas budget configured. Percentage-only by
         * design (no dollars/tokens reach end users). Shape mirrors
         * `ChatResponse.usage_allowance`: { pct_used, warn, blocked }.
         * `blocked` means the server refuses new turns until next month —
         * hosts should disable the input and show the banner. Null when
         * no budget applies (meter hidden).
         */
        usageAllowance: ref(null),
    };

    /**
     * Push a local assistant note into the current thread — no model call,
     * no server write, never persisted to history. Same mechanism as the
     * greeting (`isSystem: true`): context the user needs to see, not a
     * turn. Used by hosts to say what just happened — e.g. "I've created
     * RM-… from the document you uploaded" when the new document opens
     * with its own Atlas panel.
     *
     * A repeat of the note already sitting at the end of the thread is
     * ignored, so a re-render or a second reconcile can't double it.
     */
    self.noteAssistant = (text) => {
        const body = `${text ?? ''}`.trim();
        if (!body) return;
        const last = self.messages.value[self.messages.value.length - 1];
        if (last && last.isSystem && last.text === body) return;
        self.messages.value.push({
            id: _cryptoRandomId(),
            role: 'assistant',
            text: body,
            timestamp: Date.now(),
            isSystem: true,
        });
    };

    // ── Init ─────────────────────────────────────────────

    /**
     * `opts.skipGreeting` starts a fresh thread WITHOUT the standard
     * "Hi <name> — I'm RoleMapper Atlas…" opener. For a thread opened by
     * Atlas itself (e.g. landing in a document it just created, where the
     * host seeds its own note via `noteAssistant`) the orientation blurb is
     * noise — the user arrived from an Atlas link, already oriented.
     */
    // What the most recent initialize() sent as `context_workflow`
    // (fresh inits only), and whether it was a fresh init at all — lets
    // maybeAutoStart spot a greeting that was fetched before the
    // surface's workflow key was knowable and re-init with it.
    let _initContextWorkflow = null;
    let _lastInitWasFresh = false;

    self.initialize = async (existingConversationId, opts = {}) => {
        // Minimum hold on the "Atlas is orienting" splash screen so the
        // animated compass has real presence — even when /agent/init
        // resolves in under 200ms.
        //
        //   - Resumed conversations: no hold (user switched intentionally
        //     and expects immediate messages).
        //   - First-time visitors in this browser: longer hold (2s) so
        //     they get time to absorb the branding moment.
        //   - Returning visitors opening a fresh chat: short hold (1s)
        //     so the entrance still feels deliberate but doesn't
        //     frustrate repeat users.
        //
        // Tracked via localStorage flag `atlas_splash_seen`.
        let firstVisit = false;
        try {
            firstVisit =
                typeof localStorage !== 'undefined' &&
                localStorage.getItem('atlas_splash_seen') !== '1';
        } catch {
            // localStorage can throw (Safari private mode, etc.) — fall
            // back to the short-hold path, which is still a good UX.
        }
        const INITIALISING_MIN_MS = existingConversationId || opts.skipHold
            ? 0
            : firstVisit
                ? 2000
                : 1000;
        const initStartedAt = Date.now();
        try {
            self.status.value = 'initialising';
            self.messages.value = [];
            self.history.value = [];

            // Defer one microtask so hooks the host assigns synchronously
            // after creating the composable (contextWorkflow) are visible
            // to the creation-time init call — agentChat() fires
            // initialize() before the host's setup code has run.
            await Promise.resolve();

            const params = new URLSearchParams();
            _lastInitWasFresh = !existingConversationId;
            if (existingConversationId) {
                params.set('conversation_id', existingConversationId);
            } else {
                // Fresh conversation on a surface with a context workflow:
                // tell the server, so the greeting can be the workflow's
                // authored intro message. Resumes keep their history as-is.
                const workflowKey =
                    typeof self.contextWorkflow === 'function' ? self.contextWorkflow() : null;
                if (workflowKey) params.set('context_workflow', workflowKey);
                // Remembered so maybeAutoStart can detect a greeting that
                // was fetched before the key was knowable (see there).
                _initContextWorkflow = workflowKey || null;
            }
            const qs = params.toString();
            const url = qs ? `/agent/init?${qs}` : '/agent/init';
            const resp = await http.get(url);
            const data = resp.data;

            self.history.value = data.history;
            self.availableTools.value = data.available_tools;
            self.session.value = data.session;
            // Populate the context meter on load for resumed chats (the
            // server estimates it via count_tokens). Null/absent on fresh
            // sessions or older servers — meter stays hidden until a turn.
            self.contextUsage.value = data.context_usage ?? null;
            // Allowance state on load so warnings/blocks show before the
            // user types. Absent = no budget for this client.
            self.usageAllowance.value = data.usage_allowance ?? null;

            if (existingConversationId) {
                self.conversationId.value = existingConversationId;
                _replayHistoryIntoMessages(self, data.history);
            } else {
                self.conversationId.value = _cryptoRandomId();
                if (!opts.skipGreeting) {
                    self.messages.value.push({
                        id: _cryptoRandomId(),
                        role: 'assistant',
                        text: data.greeting,
                        timestamp: Date.now(),
                        isSystem: true,
                        isLatestAssistant: false,
                        isLatestUiBlocks: false,
                    });
                }
            }

            // Hold the loading state until the minimum has elapsed.
            const elapsed = Date.now() - initStartedAt;
            if (elapsed < INITIALISING_MIN_MS) {
                await new Promise((resolve) =>
                    setTimeout(resolve, INITIALISING_MIN_MS - elapsed),
                );
            }
            // Mark the splash as seen so subsequent visits get the shorter
            // hold. Safe-guarded for storage failures (private mode etc.).
            if (firstVisit) {
                try {
                    localStorage.setItem('atlas_splash_seen', '1');
                } catch {
                    /* ignore */
                }
            }
            self.status.value = 'idle';
            // Populate sidebar lists in the background — non-blocking.
            self.loadConversations().catch(() => {});
            self.loadModels().catch(() => {});
        } catch (err) {
            self.error.value = _humaniseError(err);
            self.status.value = 'error';
        }
    };

    // ── State mutators (same entry points REST and socket transports call) ──

    /**
     * Turn id for the currently-composing assistant reply. Stamped on the
     * /agent/chat POST and echoed on every socket event from the backend so
     * we can drop stale events after a user interruption or reconnect race.
     */
    let activeTurnId = null;
    // Background-message payloads that landed while a turn was still
    // composing. We hold them here and flush AFTER `_finaliseComposing`
    // pushes the originating assistant message — otherwise a chain that
    // completes mid-stream would slot its narration ABOVE the turn that
    // kicked it off, which reads as out-of-order in the chat.
    const pendingBackgroundMessages = [];

    // Card-only turn in flight (workflow auto-start): the greeting has
    // already introduced the surface and the card carries its own prompt,
    // so the model's narration would triple up — text deltas are dropped
    // and the finalised bubble renders blocks only (see sendMessage /
    // _finaliseComposing).
    let suppressComposingText = false;

    function _beginComposing(turnId, opts = {}) {
        activeTurnId = turnId || null;
        suppressComposingText = opts.suppressText === true;
        self.composing.value = { text: '', toolCalls: [] };
    }

    function _applyTextDelta(turnId, delta) {
        if (!self.composing.value) return;
        if (suppressComposingText) return;
        if (activeTurnId && turnId && activeTurnId !== turnId) return;
        self.composing.value.text += delta;
    }

    function _applyToolStart(turnId, record) {
        if (!self.composing.value) return;
        if (activeTurnId && turnId && activeTurnId !== turnId) return;
        self.composing.value.toolCalls.push({
            name: record.name,
            input: record.input,
            status: 'running',
        });
    }

    function _applyToolEnd(record) {
        if (!self.composing.value) return;
        const running = self.composing.value.toolCalls.find(
            (t) => t.status === 'running' && t.name === record.name,
        );
        if (running) {
            running.status = record.ok ? 'ok' : 'error';
            running.duration_ms = record.duration_ms;
            running.error = record.error;
            // Capture the tool's return data on the matching running call
            // so _finaliseComposing can hoist chain execution ids onto the
            // assistant message for inline progress-chip rendering.
            if (record.data !== undefined) running.data = record.data;
        } else {
            self.composing.value.toolCalls.push({
                name: record.name,
                input: record.input,
                status: record.ok ? 'ok' : 'error',
                duration_ms: record.duration_ms,
                error: record.error,
                data: record.data,
            });
        }
    }

    function _finaliseComposing(finalText, followUps, entityInstances, pageAction, uiBlocks) {
        if (!self.composing.value) return;
        // Card-only turn (workflow auto-start): drop the narration ONLY
        // when the card actually arrived — a failed start keeps the
        // model's explanation of what went wrong.
        const suppressText =
            suppressComposingText && Array.isArray(uiBlocks) && uiBlocks.length > 0;
        suppressComposingText = false;
        if (suppressText) {
            finalText = '';
            self.composing.value.text = '';
            // The card is the affordance — follow-up chips under a
            // card-only turn would compete with its own options.
            followUps = undefined;
        }
        const toolCalls = self.composing.value.toolCalls;
        // Hoist run_prompt_chain execution ids onto the message so the
        // bubble can render a live progress chip for each one. The chip
        // reads its state reactively from `chainProgress`, so it stays in
        // sync as the chain progresses — without needing to push any
        // chain-specific state into history.
        const chainExecutions = toolCalls
            .filter((tc) => tc.name === 'run_prompt_chain' && tc.data)
            .map((tc) => {
                const d = tc.data ?? {};
                const executionId = Number(d.execution_id);
                if (!Number.isFinite(executionId) || executionId <= 0) return null;
                return {
                    execution_id: executionId,
                    chain_name:
                        typeof d.chain_name === 'string' ? d.chain_name : null,
                };
            })
            .filter((x) => x !== null);

        // Preserve the mid-loop reasoning monologue. Each tool-use iteration
        // streams its own "let me try X, that didn't work, I'll try Y" text
        // via agent:text_delta events — those deltas accumulate into
        // composing.value.text. The REST response only carries the FINAL
        // iteration's text, so without this the intermediate thinking is
        // wiped the moment the answer pins.
        //
        // IMPORTANT: the accumulated stream includes the raw
        // `\`\`\`followups` fence (and anything after it) while the
        // final REST text has already been run through extractFollowUps
        // on the server. Without normalising the accumulated text, the
        // suffix-match below fails and the else-branch copies the
        // entire answer into the trail, making "reasoning" read as a
        // duplicate of the output — the user-visible bug.
        const cleanedAccumulated = cleanStreamingText(
            self.composing.value.text || '',
        ).trim();
        const finalTrimmed = (finalText || '').trim();
        let thinkingTrail = null;
        if (cleanedAccumulated && cleanedAccumulated !== finalTrimmed) {
            // Usual case: stream ended with the final-iteration text,
            // everything before it is the reasoning prefix.
            if (finalTrimmed && cleanedAccumulated.endsWith(finalTrimmed)) {
                thinkingTrail = cleanedAccumulated
                    .slice(0, cleanedAccumulated.length - finalTrimmed.length)
                    .trim();
            } else if (
                finalTrimmed &&
                cleanedAccumulated.includes(finalTrimmed)
            ) {
                // Final landed somewhere in the middle — rare but can
                // happen if the backend appended a trailing newline or
                // post-rendered a chart block. Take everything BEFORE
                // the final and use that as the trail; trailing content
                // after the final is shown via the committed answer
                // bubble itself, not the reasoning box.
                const idx = cleanedAccumulated.indexOf(finalTrimmed);
                thinkingTrail = cleanedAccumulated.slice(0, idx).trim();
            } else {
                // No overlap at all (heavy reformat or stream/rest
                // divergence). Keep the whole accumulated as trail —
                // better than silently losing it.
                thinkingTrail = cleanedAccumulated;
            }
            // Treat near-identical trails as nothing to show — if the
            // prefix is a small fragment (< 16 chars) it's almost
            // always stream/final whitespace drift, not real thinking.
            if (!thinkingTrail || thinkingTrail.length < 16) {
                thinkingTrail = null;
            } else if (thinkingTrail === finalTrimmed) {
                // Belt-and-braces against any other path that produces
                // an identical trail — never show the same text twice.
                thinkingTrail = null;
            }
        }

        self.messages.value.push({
            id: _cryptoRandomId(),
            role: 'assistant',
            text: finalText || self.composing.value.text,
            toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
            followUps: followUps && followUps.length > 0 ? followUps : undefined,
            chainExecutions: chainExecutions.length > 0 ? chainExecutions : undefined,
            thinkingTrail: thinkingTrail || undefined,
            entityInstances:
                Array.isArray(entityInstances) && entityInstances.length > 0
                    ? entityInstances
                    : undefined,
            // Per-message page action envelope. Stored on the bubble so a
            // user can re-trigger an action whose handler-owned UI was
            // dismissed (e.g. re-open the suggestion review panel after
            // closing it). Set at push time so Vue 2 picks it up
            // reactively — adding it after the fact wouldn't.
            pageAction: pageAction || undefined,
            // Engine-authored interactive components (workflow steps).
            // Rendered beneath the bubble; interactive only while this is
            // the latest assistant message (superseded steps go inert).
            uiBlocks:
                Array.isArray(uiBlocks) && uiBlocks.length > 0
                    ? uiBlocks
                    : undefined,
            // Page-context kind this turn was finalised under (e.g.
            // 'documents.grid', 'document'). Stamped on the MESSAGE, not
            // captured at component mount: the same message object gets
            // remounted across hosts (a document's inline Atlas panel
            // unmounting hands the conversation to the global flyout,
            // which re-renders it under the restored grid context), and a
            // mount-time capture would re-classify a doc-born message as
            // grid-born. ChatMessage's auto-narrow gate reads this.
            contextKind: self.pageContext.value?.kind ?? null,
            timestamp: Date.now(),
            // Seeded so _markLatestAssistant can flip it with a plain
            // assignment — see the note there.
            isLatestAssistant: false,
            isLatestUiBlocks: false,
        });
        _markLatestAssistant(self);
        self.composing.value = null;
        activeTurnId = null;
        // NOTE: we intentionally do NOT clear chainProgress here. Chains
        // launched by run_prompt_chain outlive the turn that started them
        // — they run in the background and land as their own assistant
        // message via `agent:background_message` when complete. Per-entry
        // removal happens in the terminal-status branch of the chain
        // progress listener below.
        //
        // EXCEPTION: chains that ran synchronously INSIDE this turn (they
        // reached a terminal state while the turn was still composing —
        // the doc_upload inline run). Their whole lifecycle was already
        // shown in the chat, so the leftover agents-panel chip is pure
        // duplication — drop those entries now.
        const kept = {};
        for (const [id, entry] of Object.entries(self.chainProgress.value)) {
            if (entry?.dismissOnFinalise !== true) kept[id] = entry;
        }
        if (Object.keys(kept).length !== Object.keys(self.chainProgress.value).length) {
            self.chainProgress.value = kept;
        }

        // Flush any narration messages (real or pending placeholders)
        // that arrived while this turn was composing — queued so they'd
        // land BELOW the originating assistant bubble we just pinned,
        // preserving the intended chat order.
        while (pendingBackgroundMessages.length > 0) {
            const next = pendingBackgroundMessages.shift();
            if (next.__pending) {
                _pushPendingNarration(next);
            } else {
                _pushBackgroundMessage(next);
            }
        }
    }

    /**
     * Append a narrated-chain-completion message to the thread as a new
     * assistant bubble. Shared by the live socket listener (when no
     * turn is composing) and the flush that runs after _finaliseComposing
     * (for narrations that arrived mid-turn and were queued).
     *
     * Intentionally does NOT clear the matching chain-progress entry —
     * the terminal-status branch in onChainProgress handles removal via
     * the linger timer; removing it immediately on narration caused the
     * chip to fall back to a generic "Done 0/?" stub the moment the
     * completion message landed.
     *
     * history.value isn't updated here — the backend's persisted history
     * is authoritative and `sendMessage` re-reads it on the next turn.
     */
    function _pushBackgroundMessage(payload) {
        // Disambiguate narration source: prompt-chain completion vs
        // document-creation completion. The two streams share the
        // pending/delta/final event shape so the UI can reuse one
        // rendering path; we just track different ids and bind onto
        // different progress maps.
        const executionId = payload.chain_execution_id;
        const docCreationRequestId = payload.doc_creation_request_id;
        const kind = payload.kind ?? (docCreationRequestId != null ? 'doc_creation' : 'chain');

        // A SUCCESSFUL document creation just landed — hand the created
        // docs to the host (both hosts wire this to the standard
        // document-open flow so the new document opens for review).
        // Gated on the server-stamped status so a failed/partial run
        // never auto-opens anything.
        if (
            kind === 'doc_creation' &&
            payload.status === 'Complete' &&
            Array.isArray(payload.documents) &&
            payload.documents.length > 0 &&
            typeof self.onDocumentsCreated === 'function'
        ) {
            try {
                self.onDocumentsCreated(payload.documents);
            } catch (err) {
                // Host hook — never let it break the narration push.
                // eslint-disable-next-line no-console
                console.error('[agentChat] onDocumentsCreated hook threw', err);
            }
        }

        // Swap-in-place if we already pushed a placeholder.
        const existing = self.messages.value.find((m) => {
            if (!m.pendingNarration) return false;
            if (executionId != null && m.chainExecutionId === executionId) return true;
            if (docCreationRequestId != null && m.docCreationRequestId === docCreationRequestId) return true;
            return false;
        });
        if (existing) {
            existing.text = payload.text;
            existing.pendingNarration = false;
            existing.narrationKind = kind;
            existing.hasDocumentOutput = payload.has_document_output === true;
            if (Array.isArray(payload.ui_blocks) && payload.ui_blocks.length > 0) {
                existing.uiBlocks = payload.ui_blocks;
            }
            existing.followUps =
                Array.isArray(payload.follow_ups) && payload.follow_ups.length > 0
                    ? payload.follow_ups
                    : undefined;
            if (Array.isArray(payload.documents) && payload.documents.length > 0) {
                existing.documents = payload.documents;
            }
            existing.timestamp = Date.now();
            _markLatestAssistant(self);
            _bindNarrationMessage(kind, executionId, docCreationRequestId, existing.id);
            // Mirror the fresh-push path: register a "Queued" panel entry
            // for any chain narration that arrived document-formatter
            // shaped, so the Run button shows up in both places.
            if (
                kind === 'chain' &&
                payload.has_document_output === true &&
                executionId != null
            ) {
                const readyEntry = self.readyToCreate.value[executionId];
                self.readyToCreate.value = {
                    ...self.readyToCreate.value,
                    [executionId]: {
                        execution_id: executionId,
                        chain_name: payload.chain_name ?? readyEntry?.chain_name ?? null,
                        narrationMessageId: existing.id,
                        kickedOff: readyEntry?.kickedOff ?? false,
                    },
                };
            }
            return;
        }

        const newMsg = {
            id: _cryptoRandomId(),
            role: 'assistant',
            text: payload.text,
            chainExecutionId: executionId ?? null,
            docCreationRequestId: docCreationRequestId ?? null,
            narrationKind: kind,
            documents: Array.isArray(payload.documents) ? payload.documents : undefined,
            // Workflow phase cards posted by the doc_upload flow ride
            // background messages — same field live turns use.
            uiBlocks:
                Array.isArray(payload.ui_blocks) && payload.ui_blocks.length > 0
                    ? payload.ui_blocks
                    : undefined,
            // When the chain's output is document-formatter shaped the
            // backend flags the narration payload; the UI picks this
            // up to render the prominent "Create Document" CTA.
            hasDocumentOutput: payload.has_document_output === true,
            followUps:
                Array.isArray(payload.follow_ups) && payload.follow_ups.length > 0
                    ? payload.follow_ups
                    : undefined,
            // Birth context — see the matching stamp in _finaliseComposing.
            contextKind: self.pageContext.value?.kind ?? null,
            timestamp: Date.now(),
            isLatestAssistant: false,
            isLatestUiBlocks: false,
        };
        self.messages.value.push(newMsg);
        _markLatestAssistant(self);
        _bindNarrationMessage(kind, executionId, docCreationRequestId, newMsg.id);

        // If this narration flags a document-formatter output, register
        // it as "Queued — ready to create" in the Document Creation
        // Agent panel so the user can fire it from there without
        // scrolling back to the chat bubble CTA. Keyed by the chain's
        // execution_id so the chat CTA and panel strip stay in sync.
        if (
            kind === 'chain' &&
            payload.has_document_output === true &&
            executionId != null
        ) {
            const entry = self.readyToCreate.value[executionId];
            self.readyToCreate.value = {
                ...self.readyToCreate.value,
                [executionId]: {
                    execution_id: executionId,
                    chain_name: payload.chain_name ?? entry?.chain_name ?? null,
                    narrationMessageId: newMsg.id,
                    kickedOff: entry?.kickedOff ?? false,
                },
            };
        }
    }

    /**
     * Record the narration bubble's id on the matching progress-map
     * entry so the chip's "View output" button can scroll the
     * conversation to it. One binder for both narration kinds —
     * dispatches based on which id is set.
     */
    function _bindNarrationMessage(kind, executionId, docCreationRequestId, messageId) {
        if (executionId != null) _bindNarrationToChain(executionId, messageId);
        if (docCreationRequestId != null) _bindNarrationToDocCreation(docCreationRequestId, messageId);
    }

    function _bindNarrationToChain(executionId, messageId) {
        const entry = self.chainProgress.value[executionId];
        if (!entry) return;
        self.chainProgress.value = {
            ...self.chainProgress.value,
            [executionId]: { ...entry, narrationMessageId: messageId },
        };
    }

    function _bindNarrationToDocCreation(requestId, messageId) {
        const entry = self.docCreationProgress.value[requestId];
        if (!entry) return;
        self.docCreationProgress.value = {
            ...self.docCreationProgress.value,
            [requestId]: { ...entry, narrationMessageId: messageId },
        };
    }

    /**
     * Push (or queue) a pending placeholder bubble for a background
     * narration being synthesised on the backend. Handles both
     * prompt-chain completions and document-creation completions via
     * the same bubble shape — `narrationKind` tells the UI which label
     * to show on the spinner ("Rendering output…" vs "Finalising
     * document…").
     */
    function _pushPendingNarration(payload) {
        const executionId = payload.chain_execution_id;
        const docCreationRequestId = payload.doc_creation_request_id;
        const kind = payload.kind ?? (docCreationRequestId != null ? 'doc_creation' : 'chain');

        if (executionId == null && docCreationRequestId == null) return;

        // Guard against duplicates on whichever id is set.
        const alreadyExists = self.messages.value.some((m) => {
            if (!m.pendingNarration) return false;
            if (executionId != null && m.chainExecutionId === executionId) return true;
            if (docCreationRequestId != null && m.docCreationRequestId === docCreationRequestId) return true;
            return false;
        });
        if (alreadyExists) return;

        const newMsg = {
            id: _cryptoRandomId(),
            role: 'assistant',
            text: '',
            pendingNarration: true,
            narrationKind: kind,
            chainExecutionId: executionId ?? null,
            docCreationRequestId: docCreationRequestId ?? null,
            chainName: payload.chain_name ?? null,
            // Birth context — see the matching stamp in _finaliseComposing.
            // Kept when the real narration swaps in: the placeholder's
            // appearance is when the bubble was "born" for context purposes.
            contextKind: self.pageContext.value?.kind ?? null,
            timestamp: Date.now(),
            isLatestAssistant: false,
            isLatestUiBlocks: false,
        };
        self.messages.value.push(newMsg);
        _bindNarrationMessage(kind, executionId, docCreationRequestId, newMsg.id);
    }

    /**
     * Clear follow-ups from any earlier assistant message — once the user
     * has asked a new question, stale chips from previous turns should
     * disappear so only the most recent response's suggestions are visible.
     */
    function _clearStaleFollowUps() {
        for (const msg of self.messages.value) {
            if (msg.followUps) msg.followUps = undefined;
        }
    }

    // ── Send a message ──────────────────────────────────

    /**
     * Context auto-start: hosts assign a function returning the kickoff
     * message text (or null) for the CURRENT context — e.g. the
     * `[workflow:start]` marker for a route whose meta declares an
     * `atlas_context_workflow`. The composable never decides WHEN a
     * context applies; hosts call `maybeAutoStart()` when their chat
     * surface is actually visible with a fresh conversation (so a
     * closed flyout doesn't mint junk conversations).
     */
    self.autoStart = null;
    let _autoStartedConversationId = null;

    /**
     * Host-assigned: a function returning the current surface's context
     * workflow key (the route-meta `atlas_context_workflow` value, gated
     * the same way autoStart is), or null. Sent to /agent/init on FRESH
     * conversations as `context_workflow`, so the server can open with
     * the workflow's authored intro message instead of the generic
     * greeting. Same hook pattern as `autoStart`; hosts assign both from
     * the same contextWorkflowKey() helper.
     */
    self.contextWorkflow = null;

    /**
     * Host hook: called with the created documents (slim
     * `{ doc_id, reference, doc_name }` rows) when a document-creation
     * run completes successfully. Hosts assign it to open the new
     * document for review; null = no post-creation behaviour.
     */
    self.onDocumentsCreated = null;

    self.maybeAutoStart = () => {
        if (self.status.value !== 'idle') return;
        if (self.conversationId.value === _autoStartedConversationId) return;
        // Only genuinely fresh conversations — anything the user (or a
        // previous auto-start replayed from history) has touched is not
        // ours to hijack.
        if (self.messages.value.some((m) => m.role === 'user')) return;
        // The start marker renders no user BUBBLE, so a resumed auto-start
        // conversation has no user message to trip the guard above — check
        // the round-tripped history too, or the workflow would start again
        // on every resume.
        if (
            self.history.value.some(
                (m) =>
                    m.role === 'user' &&
                    typeof m.content === 'string' &&
                    m.content.startsWith('[workflow:start]'),
            )
        ) {
            return;
        }
        // The greeting is fetched at initialise time, but on some surfaces
        // the context workflow isn't knowable that early (the flyout
        // singleton initialises before the router resolves the landing
        // route), so a fresh conversation can carry the generic greeting
        // where the workflow's authored intro belongs. Re-init once with
        // the key — the host's idle watcher re-enters here afterwards and
        // the auto-start proceeds (the keys then match).
        const workflowKey =
            typeof self.contextWorkflow === 'function' ? self.contextWorkflow() : null;
        if (workflowKey && _lastInitWasFresh && _initContextWorkflow !== workflowKey) {
            void self.initialize(undefined, { skipHold: true });
            return;
        }
        const text = typeof self.autoStart === 'function' ? self.autoStart() : null;
        if (!text) return;
        _autoStartedConversationId = self.conversationId.value;
        void self.sendMessage(text);
    };

    self.sendMessage = async (text) => {
        const trimmed = (text || '').trim();
        if (!trimmed || self.status.value === 'sending') return;

        self.error.value = null;
        self.status.value = 'sending';
        _clearStaleFollowUps();

        // Collect attachments that finished uploading; drop anything still in
        // flight or errored (UI shows those separately — they aren't sent).
        const attachments = self.pendingAttachments.value.slice();
        self.pendingAttachments.value = [];
        self.uploads.value = [];

        // A [workflow:start] marker is machine-composed (auto-start, or a
        // "Start guided workflow" chip) — the user typed nothing, so no
        // user bubble renders (history still carries the full marker for
        // the model), and the response renders card-only.
        const isWorkflowStart = trimmed.startsWith('[workflow:start]');
        if (!isWorkflowStart) {
            self.messages.value.push({
                id: _cryptoRandomId(),
                role: 'user',
                text: trimmed,
                // Record attachments on the bubble so users can re-open them later
                // in the thread, not just in the input area that cleared on send.
                attachments:
                    attachments.length > 0
                        ? attachments.map(_toAttachmentDisplay)
                        : undefined,
                timestamp: Date.now(),
                isLatestAssistant: false,
                isLatestUiBlocks: false,
            });
        }

        const turnId = _cryptoRandomId();
        // Progress events later prove a run belongs to THIS conversation
        // by presenting one of these turn ids — see _ownsTurn.
        _ownTurns.set(turnId, self.conversationId.value);
        _beginComposing(turnId, { suppressText: isWorkflowStart });

        try {
            const resp = await http.post('/agent/chat', {
                message: trimmed,
                history: self.history.value,
                available_tools: self.availableTools.value,
                conversation_id: self.conversationId.value,
                attachments: attachments.length > 0 ? attachments : undefined,
                turn_id: turnId,
                // Per-turn page-context hint, set by the host page (document
                // viewer, skills filter, etc.). Sent verbatim; backend
                // injects it as a `[system:page_context]` block. Undefined
                // when the caller hasn't set any context.
                page_context: self.pageContext.value || undefined,
                // Provenance only (never prompt content) — see routeName.
                route: self.routeName.value || undefined,
            });
            const response = resp.data;

            // If the socket already applied tool_end events we'd double-count,
            // so only replay from the REST response when no tool pills are
            // present yet (REST-only mode or events-gateway disconnected).
            const hasLiveToolPills =
                self.composing.value &&
                self.composing.value.toolCalls.length > 0;
            if (!hasLiveToolPills) {
                for (const record of response.tool_calls) {
                    _applyToolEnd(record);
                }
            }

            self.history.value = response.history;
            // Context-window meter telemetry (present from servers with
            // context editing enabled; older servers omit it).
            if (response.context_usage) {
                self.contextUsage.value = response.context_usage;
            }
            // Allowance state after this turn. Only overwrite when the
            // server sent it — absent on no-budget clients and older
            // servers, and a stale meter beats a flickering one.
            if (response.usage_allowance) {
                self.usageAllowance.value = response.usage_allowance;
            }
            _finaliseComposing(
                response.text,
                response.follow_ups,
                response.entity_instances,
                response.page_action,
                response.ui_blocks,
            );
            // Surface any host-page action the agent attached to the
            // turn. Always cycle through null first so a watcher fires
            // even when the same shape lands twice in a row.
            self.lastPageAction.value = null;
            if (response.page_action && typeof response.page_action === 'object') {
                // eslint-disable-next-line no-console
                console.log(
                    '[atlas] server response carried page_action:',
                    response.page_action,
                );
                self.lastPageAction.value = response.page_action;
            } else {
                // eslint-disable-next-line no-console
                console.log(
                    '[atlas] server response had NO page_action; toolCalls=',
                    Array.isArray(response?.tool_calls)
                        ? response.tool_calls.map((t) => ({
                              name: t?.name,
                              ok: t?.ok,
                              error: t?.error,
                              data: t?.data,
                          }))
                        : 'n/a',
                );
            }
            self.status.value = 'idle';
            self.loadConversations().catch(() => {});
            // Refresh the per-page panel so a brand-new conversation
            // started against the current document appears in it without
            // the user having to re-open the flyout.
            const ctxFilter = deriveContextFilter(self.pageContext.value);
            if (ctxFilter) {
                self.loadConversationsForContext(ctxFilter).catch(() => {});
            }
        } catch (err) {
            self.composing.value = null;
            suppressComposingText = false;
            self.error.value = _humaniseError(err);
            self.status.value = 'error';
        }
    };

    /**
     * Re-run the most recent user turn. Pops the last assistant response
     * (and any tool-loop bookkeeping after the user message), restores any
     * attachments from that question, and re-invokes the agent via the
     * normal sendMessage path. No-ops if there's no prior user turn or if
     * a request is already in flight.
     *
     * We re-send the user's text rather than rerunning history in-place so
     * the backend path stays identical — no "retry" flag on the API.
     */
    self.retryLast = async () => {
        if (self.status.value === 'sending') return;
        const historyUserIdx = _findLastUserTurnInHistory(self.history.value);
        if (historyUserIdx < 0) return;

        const userMsg = self.history.value[historyUserIdx];
        const text = _extractTextFromContent(userMsg.content);
        if (!text) return;

        // Restore document attachments from the persisted content blocks into
        // pendingAttachments so sendMessage sends them alongside the text. We
        // lost the original mime kind and byte size during persistence — 'text'
        // kind plus the data length is honest and keeps the chat shape intact.
        const docs = Array.isArray(userMsg.content)
            ? userMsg.content.filter((b) => b && b.type === 'document')
            : [];
        self.pendingAttachments.value = docs.map((b) => ({
            filename: (b.title || 'document').toString(),
            kind: 'text',
            size_bytes:
                b.source && typeof b.source.data === 'string'
                    ? b.source.data.length
                    : 0,
            page_count: null,
            text:
                b.source && typeof b.source.data === 'string'
                    ? b.source.data
                    : '',
            truncated: false,
        }));

        // Trim history back to just before the user message — sendMessage
        // will re-push it along with a fresh agent response.
        self.history.value = self.history.value.slice(0, historyUserIdx);

        // Trim display: drop everything from the last user bubble onward so
        // sendMessage doesn't render a duplicate. The replayed/original
        // attachments are re-attached on the re-pushed bubble.
        let displayUserIdx = -1;
        for (let i = self.messages.value.length - 1; i >= 0; i--) {
            if (self.messages.value[i].role === 'user') {
                displayUserIdx = i;
                break;
            }
        }
        if (displayUserIdx >= 0) {
            self.messages.value = self.messages.value.slice(0, displayUserIdx);
        }

        await self.sendMessage(text);
    };

    // ── Conversation management ──────────────────────────

    self.loadConversations = async () => {
        const resp = await http.get('/agent/conversations');
        self.conversations.value = resp.data;
    };

    /**
     * Load conversations the current user has previously had against a
     * specific page surface (e.g. a single document). Backed by the
     * page_context columns persisted on `agent_conversation` — so the
     * filter is exact, not a substring guess against the message body.
     *
     * Passing null/undefined or an incomplete filter clears the
     * `contextConversations` ref — letting the flyout simply re-call this
     * on every pageContext change without branching on null.
     */
    self.loadConversationsForContext = async (filter) => {
        if (!filter || !filter.kind || !filter.ref) {
            self.contextConversations.value = [];
            return;
        }
        const params = new URLSearchParams({
            page_context_kind: String(filter.kind),
            page_context_ref: String(filter.ref),
        });
        const resp = await http.get(`/agent/conversations?${params.toString()}`);
        self.contextConversations.value = resp.data;
    };

    self.switchConversation = async (conversationId) => {
        if (self.status.value === 'sending') return;
        if (conversationId === self.conversationId.value) return;
        await self.initialize(conversationId);
    };

    self.deleteConversation = async (conversationId) => {
        await http.delete(`/agent/conversations/${encodeURIComponent(conversationId)}`);
        if (conversationId === self.conversationId.value) {
            self.reset();
        } else {
            await self.loadConversations();
        }
    };

    self.renameConversation = async (conversationId, newTitle) => {
        await http.patch(
            `/agent/conversations/${encodeURIComponent(conversationId)}`,
            { title: newTitle },
        );
        await self.loadConversations();
    };

    // ── Attachment upload ────────────────────────────────

    /**
     * Upload a single file to /agent/upload. Adds an upload entry immediately
     * (status='uploading') so the UI can render a spinner; replaces it with
     * status='attached' or 'error' when the server responds.
     */
    self.uploadFile = async (file) => {
        const id = _cryptoRandomId();
        self.uploads.value.push({
            id,
            filename: file.name,
            size_bytes: file.size,
            status: 'uploading',
            attachment: null,
        });

        try {
            const form = new FormData();
            form.append('file', file);
            const resp = await http.post('/agent/upload', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const entry = self.uploads.value.find((u) => u.id === id);
            if (entry) {
                entry.status = 'attached';
                entry.attachment = resp.data;
                entry.filename = resp.data.filename;
            }
            self.pendingAttachments.value.push(resp.data);
        } catch (err) {
            const entry = self.uploads.value.find((u) => u.id === id);
            if (entry) {
                entry.status = 'error';
                entry.error = _humaniseError(err);
            }
        }
    };

    self.removeUpload = (id) => {
        const entry = self.uploads.value.find((u) => u.id === id);
        self.uploads.value = self.uploads.value.filter((u) => u.id !== id);
        if (entry && entry.attachment) {
            self.pendingAttachments.value = self.pendingAttachments.value.filter(
                (a) => a !== entry.attachment,
            );
        }
    };

    // ── Model info (display only — model choice is client policy) ──

    self.loadModels = async () => {
        try {
            const resp = await http.get('/agent/models');
            self.availableModels.value = resp.data.models;
            // Server-authoritative: `current_id` is resolved from the
            // client's atlas_model setting. Older servers only send
            // default_id — same thing for display purposes.
            self.currentModel.value = resp.data.current_id ?? resp.data.default_id;
        } catch {
            // Non-critical — the model label just won't render.
        }
    };

    // ── Chain-chip controls ──────────────────────────────

    /**
     * Remove a chain progress entry from the panel — used by the
     * dismiss (×) button on terminal chips. Chain entries are kept
     * indefinitely after terminal so the user can jump to the output;
     * when they're done with the entry they dismiss it explicitly.
     */
    self.dismissChain = (executionId) => {
        if (executionId == null) return;
        if (!(executionId in self.chainProgress.value)) return;
        const next = { ...self.chainProgress.value };
        delete next[executionId];
        self.chainProgress.value = next;
    };

    /** Remove a document-creation progress entry — parallel to dismissChain. */
    self.dismissDocCreation = (requestId) => {
        if (requestId == null) return;
        if (!(requestId in self.docCreationProgress.value)) return;
        const next = { ...self.docCreationProgress.value };
        delete next[requestId];
        self.docCreationProgress.value = next;
    };

    // ── Document Creation Agent ──────────────────────────

    /**
     * Kick off the Document Creation Agent from a chain execution the
     * user has seen and approved. Posts to the REST shortcut (which
     * bypasses Claude and calls DocumentCreationAgentService directly),
     * then lets socket events drive panel progress + narration — same
     * way `run_prompt_chain` kickoffs work.
     *
     * Flips the matching `readyToCreate` entry's `kickedOff` flag so
     * the chat-bubble CTA and the panel's Queued strip transition
     * together from "Ready — [Run]" to "Running / handed off to the
     * live progress strip below". The entry stays in the map until the
     * first `agent:doc_creation_progress` event arrives — at which
     * point the panel's "Queued" entry is retired and the real
     * progress strip takes over.
     */
    self.createDocumentFromChain = async (executionId) => {
        if (executionId == null) {
            throw new Error('createDocumentFromChain: executionId required');
        }
        // Mark the ready entry as kicked off so the panel's Queued row
        // disables its Run button while the REST call is in flight.
        const existing = self.readyToCreate.value[executionId];
        if (existing) {
            self.readyToCreate.value = {
                ...self.readyToCreate.value,
                [executionId]: { ...existing, kickedOff: true },
            };
        }
        try {
            const resp = await http.post(
                `/agent/create-document-from-chain/${executionId}`,
                {
                    conversation_id: self.conversationId.value,
                },
            );
            return resp?.data ?? null;
        } catch (err) {
            // On failure un-flip the kickedOff flag so the user can
            // retry from the panel or the bubble CTA.
            if (existing) {
                self.readyToCreate.value = {
                    ...self.readyToCreate.value,
                    [executionId]: { ...existing, kickedOff: false },
                };
            }
            throw err;
        }
    };

    /**
     * Remove a "Queued" entry the user no longer wants to create. The
     * chain narration + artifact stay in the chat — this just clears
     * the panel slot (and hides the chat-bubble CTA for that execution
     * since the CTA derives from the same map).
     */
    self.dismissReadyToCreate = (executionId) => {
        if (executionId == null) return;
        if (!(executionId in self.readyToCreate.value)) return;
        const next = { ...self.readyToCreate.value };
        delete next[executionId];
        self.readyToCreate.value = next;
    };

    // ── Reset (new conversation) ─────────────────────────

    self.reset = (opts = {}) => {
        self.messages.value = [];
        self.history.value = [];
        self.composing.value = null;
        self.error.value = null;
        self.availableTools.value = [];
        self.session.value = null;
        self.pendingAttachments.value = [];
        self.uploads.value = [];
        self.chainProgress.value = {};
        self.docCreationProgress.value = {};
        self.readyToCreate.value = {};
        self.contextUsage.value = null;
        // NOT cleared: usageAllowance — the monthly allowance is
        // per-client, not per-conversation, so a new thread keeps it
        // (initialize() refreshes it from the server anyway).
        self.conversationId.value = _cryptoRandomId();
        // Returned so a caller can act AFTER the new thread is initialised
        // (the greeting is pushed by initialize) — e.g. seeding a context
        // note that should read as the message following the greeting.
        // `opts.skipGreeting` suppresses that opener; see initialize().
        return self.initialize(undefined, opts);
    };

    /**
     * Turn ids THIS instance minted, mapped to the conversation each was
     * sent under. Chain/doc-creation progress events are emitted
     * user-wide — every socket the user has open receives every run's
     * events, `in_chat: true` and the OWNING conversation's turn_id
     * included. Honouring the flag blindly adopted foreign runs into this
     * thread: another tab (or anyone on a shared login) completing a
     * workflow-owned creation made an idle panel show an unprompted
     * "Finalising document…" handoff strip (dev report, 2026-09-11). A
     * run is in-chat here only when its turn_id is one WE issued for the
     * CURRENT conversation; foreign runs fall back to the agents panel,
     * same as turn-less MCP runs. A reload starts a fresh registry, so a
     * run still in flight across it demotes to the panel — its narration
     * still lands in-thread (background messages ARE conversation-
     * filtered).
     */
    const _ownTurns = new Map();
    const _ownsTurn = (turnId) =>
        turnId != null && _ownTurns.get(turnId) === self.conversationId.value;

    /**
     * Whether a chainProgress / docCreationProgress ENTRY belongs to the
     * current conversation. Progress events are user-wide (every socket
     * on the login gets every run — another tab, another machine, an
     * impersonator on the same user, an MCP host), so entries for
     * foreign runs live in the same maps for the agents panel. Only
     * OWNED runs may drive this conversation's chrome (resume chip,
     * handoff line, start-workflow chip): a foreign run going live or
     * terminal was flipping another user's session mid-workflow (user
     * report, 2026-09-23 — an upload "paused" and offered Resume).
     * `in_chat` is only ever set via _ownsTurn (see onChainProgress),
     * so both checks mean "this conversation issued the run's turn".
     */
    const _ownsRun = (entry) =>
        !!entry && (entry.in_chat === true || _ownsTurn(entry.turn_id));

    // ── Socket streaming subscriptions ───────────────────

    /**
     * Bind `agent:*` events from the events-gateway into composing-state
     * mutators. Each event carries a turn_id that must match the active
     * turn; mismatches are dropped so late deltas from a prior turn can't
     * leak into the current one.
     */
    if (events) {
        const onTextDelta = (payload) => {
            if (!payload) return;
            _applyTextDelta(payload.turn_id, payload.delta || '');
        };
        const onToolStart = (payload) => {
            if (!payload) return;
            _applyToolStart(payload.turn_id, {
                name: payload.name,
                input: payload.input,
            });
        };
        const onToolEnd = (payload) => {
            if (!payload) return;
            if (activeTurnId && payload.turn_id && activeTurnId !== payload.turn_id) {
                return;
            }
            // `data` MUST be forwarded — downstream UI features bind to
            // tool-result payloads on the composing record (e.g. the
            // "Compare documents" CTA walks toolCalls[].data for doc_ids).
            // The REST fallback in sendMessage only runs when no socket
            // tool_end events landed, so dropping data here silently
            // hides everything in the normal socket-live path.
            _applyToolEnd({
                name: payload.name,
                ok: payload.ok,
                duration_ms: payload.duration_ms,
                error: payload.error,
                data: payload.data,
            });
        };
        const onChainProgress = (payload) => {
            if (!payload || !payload.execution_id) return;
            // Chain events aren't filtered by turn_id here — a chain kicked
            // off in an earlier turn keeps emitting progress across later
            // user turns until it terminates. The frontend tracks them by
            // execution_id instead.
            //
            // Entries are kept indefinitely after reaching a terminal
            // state (no auto-removal). Once the chain is done, the chip
            // gains a "View output" button that jumps the conversation
            // to the narration bubble — users can then dismiss the chip
            // manually via the dismiss control on the strip.
            const existing = self.chainProgress.value[payload.execution_id];
            const isTerminal = ['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(
                payload.status,
            );
            // Preserved across events (a reap emission carries no turn).
            const turnId = payload.turn_id ?? existing?.turn_id ?? null;
            // A run is in-chat only when its turn is one THIS conversation
            // issued (_ownsTurn). Turn-less runs (an MCP host, a headless
            // caller — live miss, 2026-08-26) and runs owned by another
            // tab/conversation on the same login (unprompted "Finalising
            // document…" strip, 2026-09-11) both render in the agents
            // panel, never inside THIS thread. `existing.in_chat` stays
            // sticky: later events for an adopted run may omit the flag.
            const inChat =
                existing?.in_chat === true ||
                (payload.in_chat === true && _ownsTurn(payload.turn_id));
            const wasTerminal =
                !!existing &&
                ['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(existing.status);
            self.chainProgress.value = {
                ...self.chainProgress.value,
                [payload.execution_id]: {
                    execution_id: payload.execution_id,
                    chain_name: payload.chain_name ?? existing?.chain_name ?? null,
                    status: payload.status,
                    // Message count at the moment a WORKFLOW-OWNED run
                    // finished successfully — its follow-on (document
                    // creation, then the created card) is on its way, and
                    // parkedWorkflow holds the resume chip until something
                    // lands after this point (see the handoff note there).
                    // Only stamped on a live transition: an entry that
                    // ARRIVES already terminal (replay) has its card in
                    // the transcript already.
                    completedAtMessageCount:
                        existing?.completedAtMessageCount ??
                        (inChat && !wasTerminal && payload.status === 'completed'
                            ? self.messages.value.length
                            : null),
                    completedAtMs:
                        existing?.completedAtMs ??
                        (inChat && !wasTerminal && payload.status === 'completed'
                            ? Date.now()
                            : null),
                    // When the last real event landed — the staleness
                    // ticker times an entry out if its source process
                    // dies (a server restart mid-run leaves the row
                    // 'running' and the strip spinning for ever).
                    lastEventAt: Date.now(),
                    // A chain that finishes WHILE its turn is still
                    // composing ran synchronously inside the chat (the
                    // composing indicator showed its live strip and the
                    // next card rides the same response) — an
                    // agents-panel chip would duplicate that, so the
                    // entry is dropped when the turn finalises.
                    dismissOnFinalise:
                        existing?.dismissOnFinalise === true ||
                        (isTerminal && self.composing.value !== null),
                    progress: payload.progress ?? {
                        total_steps: 0,
                        completed_steps: 0,
                        failed_steps: 0,
                        percentage: 0,
                    },
                    // Name of the step currently executing, when the
                    // backend knows it — renders as `Running "extract
                    // skills" (step 3 of 6)` on the progress strip.
                    current_step: payload.current_step ?? null,
                    turn_id: turnId,
                    // Workflow-owned runs: the lifecycle renders IN the
                    // conversation (in-chat strip), so the agents panel
                    // skips its duplicate chip. Sticky once seen — but
                    // only for runs THIS surface's conversation owns
                    // (see the turn guard above).
                    in_chat: inChat,
                    // Preserve narrationMessageId across subsequent progress
                    // events so the "View output" button stays wired up.
                    narrationMessageId: existing?.narrationMessageId ?? null,
                },
            };
        };

        const onBackgroundMessage = (payload) => {
            if (!payload || typeof payload.text !== 'string') return;
            // Background messages name the conversation that OWNS them.
            // A run kicked off in another conversation — or from an MCP
            // host, which has none (conversation_id null) — must not
            // land in whatever chat happens to be open; the owning
            // conversation renders it from persisted history when next
            // opened (live miss, 2026-08-26: an MCP upload's narration
            // appeared mid-thread in an open portal session).
            if (
                payload.conversation_id == null ||
                payload.conversation_id !== self.conversationId.value
            ) {
                return;
            }
            // A composing turn is in flight — defer the push so the
            // narration lands BELOW the originating message once that
            // turn finalises. Otherwise a fast chain can slot its
            // completion message above the kickoff bubble, which reads
            // as out-of-order even though the narration arrived later.
            if (self.composing.value) {
                pendingBackgroundMessages.push(payload);
                return;
            }
            _pushBackgroundMessage(payload);
        };

        const onBackgroundMessagePending = (payload) => {
            if (!payload) return;
            // Same ownership filter as onBackgroundMessage — a pending
            // bubble for another conversation's run must not appear here
            // (its delta events then no-op naturally: no bubble to find).
            if (
                payload.conversation_id == null ||
                payload.conversation_id !== self.conversationId.value
            ) {
                return;
            }
            // Accept either a chain narration or a doc-creation narration
            // (disambiguated by which id is present). Same ordering rule
            // as the real narration: if a turn is still composing, queue
            // the placeholder and flush after _finaliseComposing — the
            // spinning chip otherwise slots above the kickoff bubble and
            // reads as out-of-order.
            if (
                payload.chain_execution_id == null &&
                payload.doc_creation_request_id == null
            ) {
                return;
            }
            if (self.composing.value) {
                pendingBackgroundMessages.push({
                    __pending: true,
                    ...payload,
                });
                return;
            }
            _pushPendingNarration(payload);
        };

        const onBackgroundMessageDelta = (payload) => {
            if (!payload) return;
            if (typeof payload.delta !== 'string' || payload.delta.length === 0) return;
            // Find the pending placeholder bubble for this narration
            // source (chain OR doc-creation) and append the delta.
            // `pendingNarration: true` stays set until
            // `agent:background_message` commits the final text, so the
            // UI keeps showing the "streaming cursor" through delta flow.
            const msg = self.messages.value.find((m) => {
                if (!m.pendingNarration) return false;
                if (
                    payload.chain_execution_id != null &&
                    m.chainExecutionId === payload.chain_execution_id
                ) {
                    return true;
                }
                if (
                    payload.doc_creation_request_id != null &&
                    m.docCreationRequestId === payload.doc_creation_request_id
                ) {
                    return true;
                }
                return false;
            });
            if (!msg) return;
            msg.text = (msg.text || '') + payload.delta;
        };

        /**
         * Document Creation Agent progress. Mirror of onChainProgress
         * but keyed by doc_creation_request_id — each request is a
         * row in the right-hand "Document Creation Agent" card.
         * Entries stay until the user dismisses them (same pattern as
         * chains, so the terminal state + view-document chip persist).
         */
        const onDocCreationProgress = (payload) => {
            if (!payload || payload.doc_creation_request_id == null) return;
            // As soon as a real progress event for a chain's creation
            // lands, retire the "Queued" ready-to-create entry — the
            // live progress strip now owns the UI for this execution.
            if (
                payload.execution_id != null &&
                payload.execution_id in self.readyToCreate.value
            ) {
                const next = { ...self.readyToCreate.value };
                delete next[payload.execution_id];
                self.readyToCreate.value = next;
            }
            const existing = self.docCreationProgress.value[payload.doc_creation_request_id];
            // Same cross-surface guard as chain entries: only a turn THIS
            // conversation issued adopts the run into the thread — anything
            // else (MCP, another tab, a shared login) goes to the panel.
            const creationTurnId = payload.turn_id ?? existing?.turn_id ?? null;
            const creationInChat =
                existing?.in_chat === true ||
                (payload.in_chat === true && _ownsTurn(payload.turn_id));
            const creationWasTerminal =
                !!existing && ['Complete', 'Error', 'Failed', 'Timed Out'].includes(existing.status);
            self.docCreationProgress.value = {
                ...self.docCreationProgress.value,
                [payload.doc_creation_request_id]: {
                    doc_creation_request_id: payload.doc_creation_request_id,
                    chain_name: payload.chain_name ?? existing?.chain_name ?? null,
                    execution_id: payload.execution_id ?? existing?.execution_id ?? null,
                    status: payload.status,
                    message: payload.message ?? '',
                    // Same handoff stamp as chain entries: a successful
                    // in-chat creation's card is on its way — the resume
                    // chip holds until it (or any message) lands.
                    completedAtMessageCount:
                        existing?.completedAtMessageCount ??
                        (creationInChat && !creationWasTerminal && payload.status === 'Complete'
                            ? self.messages.value.length
                            : null),
                    completedAtMs:
                        existing?.completedAtMs ??
                        (creationInChat && !creationWasTerminal && payload.status === 'Complete'
                            ? Date.now()
                            : null),
                    // For the staleness ticker — see the chain entries.
                    lastEventAt: Date.now(),
                    progress: payload.progress ?? {
                        total: 0,
                        complete: 0,
                        failed: 0,
                        percentage: 0,
                    },
                    turn_id: creationTurnId,
                    // Chat-driven creations (doc_upload confirm) narrate
                    // their whole lifecycle in the conversation — the
                    // agents panel filters these out. Sticky across
                    // events (later events may omit the flag), but only
                    // for runs this surface's conversation owns.
                    in_chat: creationInChat,
                    // Preserved across events so the final narration can
                    // bind its message id onto the entry (for the View
                    // output jump button, mirroring chain entries).
                    narrationMessageId: existing?.narrationMessageId ?? null,
                    documents: existing?.documents ?? [],
                },
            };
        };

        events.on('agent:text_delta', onTextDelta);
        events.on('agent:tool_start', onToolStart);
        events.on('agent:tool_end', onToolEnd);
        events.on('agent:chain_progress', onChainProgress);
        events.on('agent:doc_creation_progress', onDocCreationProgress);
        events.on('agent:background_message', onBackgroundMessage);
        events.on('agent:background_message_pending', onBackgroundMessagePending);
        events.on('agent:background_message_delta', onBackgroundMessageDelta);
        // `agent:turn_start` and `agent:turn_end` are advisory — the REST
        // response handles finalisation already, so we don't need them here.
    }

    /**
     * Handoff gaps: a workflow-owned run that finished SUCCESSFULLY has
     * its follow-on already in flight — chain done → creation not yet
     * started, creation done → created card not yet posted. Both windows
     * read as "nothing live" (user reports, 2026-08-27: the resume chip
     * flashed in; 2026-08-26: ~10s of dead air before the created card).
     * True until anything lands in the transcript after the terminal
     * event. Failures never stamp `completedAtMessageCount`, so their
     * message clears this immediately. Hosts render a "Finalising
     * document…" line from this, and parkedWorkflow holds the resume
     * chip while it's true.
     */
    self.pendingHandoff = computed(() => {
        const msgCount = self.messages.value.length;
        // `staleNow` ticks so the time cap below re-evaluates: without
        // it a follow-on that never lands (its process died in the gap)
        // would show "Finalising…" and hold the resume chip for ever.
        const now = self.staleNow.value;
        // Owned runs only — a foreign run (another tab/device/surface on
        // the same login) must not suppress or trigger this
        // conversation's handoff line.
        const chains = Object.values(self.chainProgress.value).filter(_ownsRun);
        const creations = Object.values(self.docCreationProgress.value).filter(_ownsRun);
        // A LIVE run's strip owns the status display — "Finalising…"
        // covers only the GAPS between tracked runs. Without this, a
        // pipeline's completed first phase (the generate path's drafting
        // cover) showed the handoff line UNDER the next phase's live
        // strip: two progress indicators for one job (user report,
        // 2026-08-27). The resume chip is unaffected — parkedWorkflow
        // checks the live runs before this.
        const anyLive =
            chains.some(
                (c) => !['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(c.status),
            ) ||
            creations.some((d) => !['Complete', 'Error', 'Failed', 'Timed Out'].includes(d.status));
        if (anyLive) return false;
        return [...chains, ...creations].some(
            (e) =>
                e.completedAtMessageCount != null &&
                msgCount <= e.completedAtMessageCount &&
                (e.completedAtMs == null || now - e.completedAtMs < HANDOFF_MAX_MS),
        );
    });

    // ── Staleness ticker ──────────────────────────────────────
    // Progress entries are push-only (socket): if the process running a
    // chain/creation dies (a server restart mid-run), no terminal event
    // ever arrives and a non-terminal entry spins for ever — and keeps
    // suppressing the resume chip. The server reaps such rows, but it
    // can't reach a strip whose socket events already stopped, so the
    // client also times entries out after STALE_EVENT_MS of silence.
    // Self-healing: a later REAL event overwrites the synthetic status.
    self.staleNow = ref(Date.now());
    const staleTimer = setInterval(() => {
        self.staleNow.value = Date.now();
        const now = self.staleNow.value;
        const chains = { ...self.chainProgress.value };
        let chainsChanged = false;
        for (const [id, entry] of Object.entries(chains)) {
            if (['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(entry.status))
                continue;
            if (entry.lastEventAt != null && now - entry.lastEventAt > STALE_EVENT_MS) {
                chains[id] = { ...entry, status: 'timed_out' };
                chainsChanged = true;
            }
        }
        if (chainsChanged) self.chainProgress.value = chains;
        const creations = { ...self.docCreationProgress.value };
        let creationsChanged = false;
        for (const [id, entry] of Object.entries(creations)) {
            if (['Complete', 'Error', 'Failed', 'Timed Out'].includes(entry.status)) continue;
            if (entry.lastEventAt != null && now - entry.lastEventAt > STALE_EVENT_MS) {
                creations[id] = {
                    ...entry,
                    status: 'Timed Out',
                    message: 'Lost contact with the run — the server may have restarted.',
                };
                creationsChanged = true;
            }
        }
        if (creationsChanged) self.docCreationProgress.value = creations;
    }, 30_000);
    // Browser timers have no unref; the composable lives for the page.
    void staleTimer;

    /**
     * The conversation's PARKED workflow, if any: the newest message
     * carrying workflow cards was awaiting input but has been superseded
     * by later conversation (its card is inert). Hosts render a "Resume
     * workflow" chip from this; clicking sends a [workflow:action]
     * resume marker, which the engine answers with a fresh card.
     *
     * Emit-time approximation of the server's instance state (like card
     * interactivity): a conversational exit after the card doesn't
     * rewrite it, so marker exits/finishes after the card hide the chip
     * and the rare stale chip is answered gracefully by the engine
     * ("nothing to resume").
     */
    self.parkedWorkflow = computed(() => {
        // A LIVE chain or doc-creation run means the workflow's next
        // card (or a creation's completion card) is already on its way —
        // offering a resume now races it, and the instance may have
        // completed-in-place while the side effect runs (create-on-submit
        // forms do). Hold the chip until every tracked run is terminal.
        // OWNED runs only: progress events are user-wide, and a foreign
        // run (another tab, machine, impersonator or MCP host on the
        // same login) going live/terminal was toggling this chip in an
        // unrelated conversation mid-workflow.
        const chainLive = Object.values(self.chainProgress.value).some(
            (c) =>
                _ownsRun(c) &&
                !['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(c.status),
        );
        const creationLive = Object.values(self.docCreationProgress.value).some(
            (d) =>
                _ownsRun(d) &&
                !['Complete', 'Error', 'Failed', 'Timed Out'].includes(d.status),
        );
        if (chainLive || creationLive) return null;
        if (self.pendingHandoff.value) return null;

        const msgs = self.messages.value;
        let cardIdx = -1;
        for (let i = msgs.length - 1; i >= 0; i--) {
            const m = msgs[i];
            if (
                m.role === 'assistant' &&
                !m.isSystem &&
                Array.isArray(m.uiBlocks) &&
                m.uiBlocks.length
            ) {
                cardIdx = i;
                break;
            }
        }
        if (cardIdx === -1) return null;
        const cardMsg = msgs[cardIdx];
        // Card still live (latest message) → the card itself is the
        // affordance; no chip.
        if (cardMsg.isLatestAssistant) return null;
        const block = cardMsg.uiBlocks.find(
            (b) => b && b.workflow && b.workflow.status === 'awaiting_input',
        );
        if (!block) return null;
        const wf = block.workflow;
        // Explicitly exited/finished since (marker messages only —
        // conversational exits are caught server-side instead). A resume
        // marker counts too: a SUCCESSFUL resume lands a newer card
        // (moving cardIdx past it), so a resume marker still sitting
        // after the newest card means the resume came back card-less —
        // expired, cancelled or completed — and re-offering it would loop.
        for (let i = cardIdx + 1; i < msgs.length; i++) {
            const m = msgs[i];
            if (m.role !== 'user' || typeof m.text !== 'string') continue;
            if (
                m.text.startsWith('[workflow:action]') &&
                m.text.includes(`instance_id=${wf.instance_id}`) &&
                // `submitted` counts: a successful submit lands a newer
                // card (moving cardIdx past it), so a submitted marker
                // still sitting after the newest card means the submit
                // completed CARD-LESSLY — a create-on-submit form's
                // release (the instance completed in place; 2026-08-27
                // screenshot: "Resume Confirm placement" offered while
                // the document was being drafted).
                /action=(exit|finish|resume|submitted)\b/.test(m.text)
            ) {
                return null;
            }
        }
        return {
            instance_id: wf.instance_id,
            step_key: wf.step_key,
            label: _parkedResumeLabel(block),
        };
    });

    /**
     * True when a "Start <the page's default workflow>" chip is worth
     * offering. The fresh-only auto-start never touches a conversation
     * the user has used — and the portal keeps ONE conversation across
     * pages and surfaces, so in practice the flyout almost always opens
     * onto a used one and a page's `atlas_context_workflow` silently
     * never appeared (user report, 2026-08-27). This is the explicit,
     * non-hijacking affordance for exactly that case. False whenever a
     * workflow is already engaged (live card at the bottom, parked card
     * up-thread, workflow-owned run/handoff in flight) or the
     * conversation is fresh (auto-start owns that path — a chip would
     * just flash before the card lands).
     */
    self.canOfferWorkflowStart = computed(() => {
        // Fresh conversation: auto-start territory.
        if (!self.messages.value.some((m) => m.role === 'user')) return false;
        // Owned runs only — see parkedWorkflow.
        const chainLive = Object.values(self.chainProgress.value).some(
            (c) =>
                _ownsRun(c) &&
                !['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(c.status),
        );
        const creationLive = Object.values(self.docCreationProgress.value).some(
            (d) =>
                _ownsRun(d) &&
                !['Complete', 'Error', 'Failed', 'Timed Out'].includes(d.status),
        );
        if (chainLive || creationLive || self.pendingHandoff.value) return false;
        if (self.parkedWorkflow.value) return false;
        // A live awaiting-input card at the bottom IS the affordance.
        const msgs = self.messages.value;
        for (let i = msgs.length - 1; i >= 0; i--) {
            const m = msgs[i];
            if (m.role !== 'assistant' || m.isSystem) continue;
            if (Array.isArray(m.uiBlocks) && m.uiBlocks.length) {
                if (
                    m.isLatestAssistant &&
                    m.uiBlocks.some((b) => b && b.workflow && b.workflow.status === 'awaiting_input')
                ) {
                    return false;
                }
            }
            break;
        }
        return true;
    });

    // Fire init immediately — page shows loading state until it completes.
    self.initialize();

    return self;
}

// ── Public helpers (exported for UI consumers) ───────────

/**
 * Strip an in-flight trailing ```followups fence + everything after it
 * from streaming text. Claude emits the follow-up suggestions at the very
 * end of its response; during streaming the fence arrives partially and
 * shouldn't leak into the rendered bubble. The backend's extractFollowUps()
 * removes the same block from the final text, so this keeps streaming and
 * final-render visually identical.
 *
 * Also strip any unclosed trailing fenced block (``` ... without a closing
 * fence) so we don't render a half-parsed code block during the pause
 * between the opening fence and the first line of content.
 */
export function cleanStreamingText(text) {
    if (!text) return '';
    // Chop off an in-flight followups block — open fence alone is enough to hide.
    const followupsIdx = text.lastIndexOf('```followups');
    if (followupsIdx !== -1) {
        // Check if a matching closing fence comes after — if so keep them both
        // and let markdown-it render normally (final message case).
        const afterOpen = text.slice(followupsIdx + '```followups'.length);
        if (!afterOpen.includes('```')) {
            return text.slice(0, followupsIdx).trimEnd();
        }
    }
    return text;
}

// ── Private helpers ──────────────────────────────────────

/**
 * Rebuild display messages from stored history.
 *
 * Skips:
 *   1. [system:init] — the hidden backend context block.
 *   2. Assistant messages with tool_use blocks — mid-loop monologue that was
 *      never shown to the user originally.
 *   3. tool_result user messages — agent-loop bookkeeping (empty after text extraction).
 */
function _replayHistoryIntoMessages(self, history) {
    // Track the most recently pushed assistant message so only the LAST one
    // keeps its follow-up chips — older turns are conversation history, not
    // live prompts the user is still deciding between.
    let lastAssistantMessage = null;
    // A [workflow:start] marker was seen and its answering assistant turn
    // hasn't landed yet — that turn replays card-only (same rule as the
    // live path: the narration would triple up with the intro + card).
    let autoStartPending = false;

    for (const msg of history) {
        // Skip assistant turns that include a tool_use block — mid-loop.
        if (msg.role === 'assistant' && Array.isArray(msg.content)) {
            const hasToolUse = msg.content.some((b) => b && b.type === 'tool_use');
            if (hasToolUse) continue;
        }

        const rawText = _extractTextFromContent(msg.content);
        if (!rawText) continue;

        // Filter synthetic user prompts that were never shown to the user:
        // - [system:init] — the session-context block seeded at init()
        // - [system:chain_complete] — the narration-prompt seeded when a
        //   background chain finishes (see AgentService.narrateChainCompletion)
        // - [workflow:start] — the machine-composed auto-start marker
        //   (renders no user bubble on the live path either)
        if (
            msg.role === 'user' &&
            (rawText.startsWith('[system:init]') ||
                rawText.startsWith('[system:chain_complete]'))
        ) {
            continue;
        }
        if (msg.role === 'user' && rawText.startsWith('[workflow:start]')) {
            autoStartPending = true;
            continue;
        }

        // Assistant turns were persisted with the raw ```followups fence still
        // attached (the backend strips it only from the REST response, not
        // from history). Strip it here so the bubble renders clean text and
        // the follow-up questions come back as clickable chips.
        let text = rawText;
        let followUps;
        if (msg.role === 'assistant') {
            const extracted = _extractFollowUpsFromText(rawText);
            text = extracted.text;
            followUps = extracted.followUps.length > 0 ? extracted.followUps : undefined;
        }

        // User messages may carry document blocks (attachments sent at send
        // time). Reconstruct display chips from what's persisted — we get
        // the filename from `title` and the extracted text from `source.data`.
        // We don't know the original kind or byte size on resume; defaults
        // below are honest and good enough for the viewer.
        let attachments;
        if (msg.role === 'user' && Array.isArray(msg.content)) {
            const docs = msg.content.filter((b) => b && b.type === 'document');
            if (docs.length > 0) {
                attachments = docs.map((b) => ({
                    filename: (b.title || 'document').toString(),
                    kind: 'text',
                    size_bytes:
                        b.source && typeof b.source.data === 'string'
                            ? b.source.data.length
                            : 0,
                    page_count: null,
                    text:
                        b.source && typeof b.source.data === 'string'
                            ? b.source.data
                            : '',
                }));
            }
        }

        // The backend stamps entity_instances onto final assistant messages
        // in persisted history JSON so resumed conversations can rebuild the
        // Compare button without re-walking tool_result payloads. Pass it
        // through under the camelCase name the message-bound UI expects.
        const entityInstances =
            msg.role === 'assistant' &&
            Array.isArray(msg.entity_instances) &&
            msg.entity_instances.length > 0
                ? msg.entity_instances
                : undefined;

        // ui_blocks are stamped alongside entity_instances on final
        // assistant messages. Restored so replayed conversations re-render
        // the workflow components (inert unless still the latest turn).
        const uiBlocks =
            msg.role === 'assistant' &&
            Array.isArray(msg.ui_blocks) &&
            msg.ui_blocks.length > 0
                ? msg.ui_blocks
                : undefined;

        // The assistant turn answering a [workflow:start] marker replays
        // card-only, matching the live path. A card-less answer (the start
        // failed) keeps its text — that explanation is all the user has.
        if (msg.role === 'assistant' && autoStartPending) {
            autoStartPending = false;
            if (uiBlocks) {
                text = '';
                followUps = undefined;
            }
        }

        const entry = {
            id: _cryptoRandomId(),
            role: msg.role,
            text,
            attachments,
            followUps,
            entityInstances,
            uiBlocks,
            // Replayed messages belong to the conversation being loaded
            // for the CURRENT context (init/switchConversation run after
            // the context settles), so the live kind is their birth
            // context — this is what lets a grid conversation's last
            // assistant turn re-narrow the grid on reload, while a doc
            // conversation replayed inside a document stays inert.
            contextKind: self.pageContext.value?.kind ?? null,
            timestamp: Date.now(),
            isLatestAssistant: false,
            isLatestUiBlocks: false,
        };
        self.messages.value.push(entry);
        if (msg.role === 'assistant') {
            if (lastAssistantMessage) lastAssistantMessage.followUps = undefined;
            lastAssistantMessage = entry;
        }
    }
    _markLatestAssistant(self);
}

/**
 * Stamp the most recent non-system assistant message with
 * `isLatestAssistant: true` and clear the flag on every other. The flag
 * drives the Retry button in the UI — binding to a flat boolean avoids
 * subtle reactivity issues with deriving "last one" from a computed that
 * iterates the array on every render.
 */
function _markLatestAssistant(self) {
    let latest = null;
    for (let i = self.messages.value.length - 1; i >= 0; i--) {
        const m = self.messages.value[i];
        if (m.role === 'assistant' && !m.isSystem) {
            latest = m;
            break;
        }
    }
    // The newest assistant message CARRYING workflow cards — usually the
    // workflow's current step. When a side conversation supersedes it
    // (so it is no longer the latest assistant message), its card shows
    // the "still active — ask to continue" hint; older answered-step
    // cards never do.
    let latestWithBlocks = null;
    for (let i = self.messages.value.length - 1; i >= 0; i--) {
        const m = self.messages.value[i];
        if (
            m.role === 'assistant' &&
            !m.isSystem &&
            Array.isArray(m.uiBlocks) &&
            m.uiBlocks.length
        ) {
            latestWithBlocks = m;
            break;
        }
    }
    const list = self.messages.value;
    for (let i = 0; i < list.length; i++) {
        const m = list[i];
        const isLatest = m === latest;
        const isLatestBlocks = m === latestWithBlocks;
        if (
            m.isLatestAssistant === isLatest &&
            m.isLatestUiBlocks === isLatestBlocks
        ) {
            continue;
        }
        if ('isLatestAssistant' in m && 'isLatestUiBlocks' in m) {
            m.isLatestAssistant = isLatest;
            m.isLatestUiBlocks = isLatestBlocks;
        } else {
            // Vue 2.7 only installs reactive getters/setters for keys that
            // existed when the object became reactive, so a plain assignment
            // to a brand-new key wouldn't re-render ChatMessage (which reads
            // the flags to gate interactivity) — superseded workflow-step
            // cards would stay live until a full reload. Every message this
            // composable pushes seeds the keys, so this is only reachable for
            // externally-pushed bubbles; replacing the array entry is tracked
            // by both Vue 2 and Vue 3. (Vue 2's `set` can't be used here —
            // it doesn't exist in Vue 3 and breaks the beta build.)
            list.splice(i, 1, {
                ...m,
                isLatestAssistant: isLatest,
                isLatestUiBlocks: isLatestBlocks,
            });
        }
    }
}

/**
 * Chip label for a parked workflow, named after WHAT is being resumed —
 * "Resume upload" beats "Resume workflow". An engine-stamped
 * `workflow.resume_label` wins outright (the engine-owned gate cards
 * are generic `choice` blocks whose type names nothing). Otherwise
 * derived from the parked card's raw block: the type names most steps;
 * a form borrows its submit_label ("Create profile" → "Resume Create
 * profile") unless that's just "Continue".
 */
function _parkedResumeLabel(block) {
    const stamped = block?.workflow?.resume_label;
    if (typeof stamped === 'string' && stamped.trim()) return stamped.trim();
    const type = block?.type;
    if (type === 'doc_upload') return 'Resume upload';
    if (type === 'job_search') return 'Resume job search';
    if (type === 'doc_list') {
        // The create gate is a doc_list whose footer re-SUBMITS the held
        // values ("Create anyway") — it resumes as a decision, not a pick.
        return block?.props?.footer_action?.action === 'submitted'
            ? 'Resume duplicate check'
            : 'Resume document pick';
    }
    if (type === 'doc_view') return 'Resume document review';
    if (type === 'form') {
        const submit =
            typeof block?.props?.submit_label === 'string'
                ? block.props.submit_label.trim()
                : '';
        if (submit && !/^continue$/i.test(submit)) return `Resume ${submit}`;
        return 'Resume form';
    }
    return 'Resume workflow';
}

/**
 * Mirror of the backend's extractFollowUps — parse out a trailing
 * ```followups JSON block and return the cleaned text + parsed array.
 * Malformed blocks are dropped (stripped from the text, no chips shown).
 */
function _extractFollowUpsFromText(text) {
    const match = text.match(/```followups\s*([\s\S]*?)```/i);
    if (!match) return { text, followUps: [] };
    const cleaned = text.replace(match[0], '').trimEnd();
    try {
        const parsed = JSON.parse(match[1].trim());
        if (!Array.isArray(parsed)) return { text: cleaned, followUps: [] };
        const followUps = parsed
            .filter((q) => typeof q === 'string')
            .map((q) => q.trim())
            .filter((q) => q.length > 0)
            .slice(0, 5);
        return { text: cleaned, followUps };
    } catch {
        return { text: cleaned, followUps: [] };
    }
}

/**
 * Project a ChatAttachment (from /agent/upload) onto the UI's display shape.
 * Identical fields today, but keeping the conversion explicit means changes
 * to the server payload don't leak into components unexpectedly.
 */
function _toAttachmentDisplay(a) {
    return {
        filename: a.filename,
        kind: a.kind,
        size_bytes: a.size_bytes,
        page_count: a.page_count,
        text: a.text,
    };
}

/**
 * Locate the most recent "real" user turn in history — a message the user
 * actually typed (not the [system:init] context block, not a tool_result
 * bookkeeping turn from the agent loop). Returns its index or -1.
 */
function _findLastUserTurnInHistory(history) {
    for (let i = history.length - 1; i >= 0; i--) {
        const msg = history[i];
        if (msg.role !== 'user') continue;
        // Skip pure tool_result bookkeeping messages (no visible text/doc).
        if (Array.isArray(msg.content)) {
            const hasUserContent = msg.content.some(
                (b) => b && (b.type === 'text' || b.type === 'document'),
            );
            if (!hasUserContent) continue;
        }
        const text = _extractTextFromContent(msg.content);
        if (text.startsWith('[system:init]')) continue;
        return i;
    }
    return -1;
}

function _extractTextFromContent(content) {
    if (typeof content === 'string') return content;
    if (!Array.isArray(content)) return '';
    return content
        .filter((b) => b && b.type === 'text' && typeof b.text === 'string')
        .map((b) => b.text)
        .join('\n')
        .trim();
}

/**
 * Mirror of the server's `deriveConversationPageContext` — projects a
 * loose `PageContext` (chosen by the host page) onto the narrow
 * `{ kind, ref }` shape the conversations filter accepts. Returns null
 * for surfaces we don't yet index, so callers can pass `pageContext.value`
 * straight in without checking `kind` themselves.
 */
export function deriveContextFilter(ctx) {
    if (!ctx || typeof ctx.kind !== 'string' || !ctx.kind) return null;
    if (ctx.kind === 'document') {
        const ref =
            ctx.data && typeof ctx.data.reference === 'string' && ctx.data.reference
                ? ctx.data.reference
                : null;
        return ref ? { kind: 'document', ref } : null;
    }
    return null;
}

function _cryptoRandomId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function _humaniseError(err) {
    if (err?.response?.data?.message) return String(err.response.data.message);
    if (err?.response?.status === 401) return 'Your session has expired. Please sign in again.';
    if (err?.response?.status === 500) return 'The agent encountered an error. Please try again.';
    if (err?.message) return String(err.message);
    return 'Something went wrong. Please try again.';
}

// ── Model preference persistence ─────────────────────────

